// src/utils/undoRedo/ChronoUndoRedoManager.js
import { generateUniqueId } from '../../utils/generateUniqueId';
import { addAction } from '../../redux/operationHistorySlice';
import { updatePanelExitPoints, calculatePanelExitCount, updatePanelWiringPositions } from '../wiring';

// Bellek koruması: undo geçmişi (transaction + komut + snapshot) sınırsız
// büyüdüğünde oturum boyu RAM tükeniyordu (OOM). Son MAX_HISTORY transaction'ı
// tut; daha eskiler artık geri alınamaz ama bellek serbest kalır.
const MAX_HISTORY = 100;

export default class ChronoUndoRedoManager {
  constructor(dispatch) {
    this.dispatch = dispatch;
    this.history = []; // 🧠 memory-only
    this.position = -1; // geçerli transaction index
    this.pendingRedo = false; // 🆕 Async redo tracking
    this.onRedoComplete = null; // 🆕 Completion callback
  }

  // 🆕 Async REDO tamamlandığında çağrılır
  notifyRedoComplete() {
    // Position'ı artır
    this.position = Math.min(this.history.length - 1, this.position + 1);
    this.pendingRedo = false;

    // Callback varsa çağır (chain advancement için)
    if (this.onRedoComplete) {
      const callback = this.onRedoComplete;
      this.onRedoComplete = null;
      callback();
    }

    this.dispatch(
      addAction({
        type: 'REDO_COMPLETE',
        timestamp: Date.now(),
      })
    );
  }

  // 🔵 Transaction başlat
  beginTransaction(label = 'Unnamed Transaction') {
    const txId = generateUniqueId('tx');
    const transaction = {
      id: txId,
      label,
      commands: [],
      timestamp: Date.now(),
    };

    // Eğer geçmişin ortasındaysak, future'ı sil (klasik undo davranışı)
    if (this.position < this.history.length - 1) {
      this.history = this.history.slice(0, this.position + 1);
    }

    this.history.push(transaction);
    this.position = this.history.length - 1;

    // Bellek koruması: en eski transaction'ları at, son MAX_HISTORY'yi tut.
    if (this.history.length > MAX_HISTORY) {
      const removeCount = this.history.length - MAX_HISTORY;
      this.history.splice(0, removeCount);
      this.position -= removeCount;
    }

    // Redux’a sadece bilgi amaçlı snapshot gönder
    this.dispatch(
      addAction({
        type: 'TRANSACTION_BEGIN',
        label,
        timestamp: transaction.timestamp,
        id: txId,
      })
    );

    return transaction;
  }

  // 🟢 Transaction'a komut ekle
  addCommand(cmd) {
    if (this.position < 0 || !this.history[this.position]) {
      return;
    }
    this.history[this.position].commands.push(cmd);

    // Redux’a sadece text snapshot
    this.dispatch(
      addAction({
        type: 'ADD_COMMAND',
        label: cmd?.type || cmd?.action || 'Unknown Command',
        timestamp: Date.now(),
      })
    );
  }

  // 🟢 Transaction'ı finalize et
  commitTransaction(transaction) {
    if (!transaction) return;

    this.dispatch(
      addAction({
        type: 'TRANSACTION_COMMIT',
        label: transaction.label,
        id: transaction.id,
        timestamp: Date.now(),
      })
    );
  }

  // 🔙 Undo
  undo(scene, dispatch) {
    

    if (this.position < 0) {
      
      return;
    }

    const tx = this.history[this.position];
    if (!tx) {
      
      return;
    }

    

    // ✅ UNDO öncesi etkilenen panelId'leri topla
    const affectedPanelIds = new Set();
    tx.commands.forEach((cmd) => {
      // sceneData veya feederData'dan panelId'yi al
      // ✅ Bell için: bellLoad.parentComponentId kullan
      const panelId = cmd.sceneData?.restoreSource?.p?.panelID ||
                      cmd.sceneData?.restoreSource?.panelID ||
                      cmd.data?.after?.feeder?.panelId ||
                      cmd.data?.after?.feeder?.sourceId ||
                      cmd.data?.before?.feeder?.panelId ||
                      cmd.data?.before?.feeder?.sourceId ||
                      cmd.data?.after?.bellLoad?.parentComponentId ||
                      cmd.data?.before?.bellLoad?.parentComponentId;
      if (panelId) {
        affectedPanelIds.add(panelId);
      }
    });

    // restoreCallback olan komutlar için pendingOutsRemoval bilgisini hazırla
    const pendingOutsRemovalData = [];

    tx.commands
      .slice() // kopya al
      .reverse()
      .forEach((cmd, index) => {
        // ✅ restoreCallback var mı kontrol et
        const hasRestoreCallback = cmd.restoreCallback && typeof cmd.restoreCallback === 'function';

        // ✅ Önce undo() çağır (sahneden silme)
        if (cmd?.undo) cmd.undo(scene, dispatch || this.dispatch);

        // ✅ UNDO: cmd.undo() panel.userData.outs'u güncellemediği için manuel temizle
        // Bu, calculatePanelExitCount() doğru değer döndürmesi için gerekli
        try {
          const feederData = cmd.data?.after?.feeder || cmd.data?.before?.feeder;
          const componentData = cmd.data?.after?.component || cmd.data?.before?.component;
          // ✅ Bell için: bellLoad kullan
          const bellLoadData = cmd.data?.after?.bellLoad || cmd.data?.before?.bellLoad;

          // ✅ affectedPanelIds ile aynı sırada olmalı
          const cmdPanelId = cmd.sceneData?.restoreSource?.p?.panelID ||
                            cmd.sceneData?.restoreSource?.panelID ||
                            feederData?.panelId ||
                            feederData?.sourceId ||
                            bellLoadData?.parentComponentId;

          if (cmdPanelId && scene) {
            let cmdPanelObj = null;
            scene.traverse((obj) => {
              if (obj.userData?.id === cmdPanelId || obj.uuid === cmdPanelId) {
                cmdPanelObj = obj;
              }
            });

            if (cmdPanelObj && cmdPanelObj.userData?.outs) {
              const loadId = componentData?.id || bellLoadData?.id;
              const actualWireId = feederData?.wireID || cmd.sceneData?.wireID || cmd.sceneData?.wireId || feederData?.id;
              const feederId = feederData?.id;
              const branchId = cmd.sceneData?.branchId || cmd.sceneData?.branchID ||
                              componentData?.metadata?.branchId ||
                              feederData?.metadata?.branchId ||
                              bellLoadData?.metadata?.branchId;

              // restoreCallback varsa outs silmeyi ERTELE (local undo bitince silinecek)
              if (hasRestoreCallback) {
                pendingOutsRemovalData.push({
                  panelId: cmdPanelId,
                  loadId,
                  actualWireId,
                  feederId,
                  branchId
                });
              } else {
                // ✅ restoreCallback yoksa hemen sil
                cmdPanelObj.userData.outs = cmdPanelObj.userData.outs.filter(out => {
                  const shouldRemove = out.branchID === branchId ||
                                       out.feederId === feederId ||
                                       out.wires?.[0]?.id === actualWireId ||
                                       out.loadId === loadId ||
                                       out.wireId === actualWireId;
                  return !shouldRemove;
                });
              }
            }
          }
        } catch (outsErr) {
          // panel.userData.outs temizleme hatası
        }

        // restoreCallback varsa ÇAĞIR (local undo moduna geç)
        if (hasRestoreCallback) {
          try {
            cmd.restoreCallback(cmd.restoreVertices, cmd.restoreSource, false, {
              ...cmd.sceneData,
              pendingOutsRemoval: pendingOutsRemovalData.length > 0 ? [...pendingOutsRemovalData] : null
            });
          } catch (err) {
            // restoreCallback hatası
          }
        }
      });

    // restoreCallback olan komut varsa wire tam silinmemiştir (local undo moduna
    // geçilir). Bu durumda panel exit point güncellemesini ERTELE.
    const hasAsyncUndo = tx.commands.some(cmd =>
      cmd.restoreCallback && typeof cmd.restoreCallback === 'function'
    );

    // Panel exit point güncellemesi: SADECE wire tam olarak silinmişse çalıştır.
    // - sync undo (restoreCallback yok): wire tam silinir, hemen güncelle.
    // - async undo (restoreCallback var, local undo moduna geçilir): wire henüz
    //   tam silinmemiş, exit point'leri silmek erken olur. Local undo bitince
    //   useBaseWiring tarafında ayrıca tetiklenir.
    // exitCount için outs.length doğrudan kullanılır → calculatePanelExitCount
    // önce sahnedeki görsel point'leri saydığı için stale eski sayıyı döner.
    if (!hasAsyncUndo && scene && affectedPanelIds.size > 0) {
      affectedPanelIds.forEach((panelId) => {
        try {
          let panelObj = null;
          scene.traverse((obj) => {
            if (obj.userData?.id === panelId) {
              panelObj = obj;
            }
          });

          if (panelObj) {
            const exitCount = Array.isArray(panelObj.userData?.outs)
              ? panelObj.userData.outs.length
              : calculatePanelExitCount(panelObj);
            updatePanelExitPoints(panelObj, exitCount, scene);
            if (exitCount > 0) {
              try { updatePanelWiringPositions(scene, panelObj); } catch (e) { /* ignore */ }
            }
          }
        } catch (err) {
          // Panel güncelleme hatası
        }
      });
    }

    this.dispatch(
      addAction({
        type: 'UNDO',
        label: tx.label,
        timestamp: Date.now(),
      })
    );

    this.position = Math.max(-1, this.position - 1);
  }

  // 🔁 Redo
  redo(scene, dispatch, onComplete = null) {
    if (this.position >= this.history.length - 1) {
      if (onComplete) onComplete();
      return;
    }

    const tx = this.history[this.position + 1];
    if (!tx) {
      if (onComplete) onComplete();
      return;
    }

    // Async command kontrolü - restoreCallback kullanan komutlar async'tir
    const hasAsyncCommand = tx.commands.some(cmd =>
      cmd.restoreCallback && typeof cmd.restoreCallback === 'function'
    );

    if (hasAsyncCommand) {
      this.pendingRedo = true;
      this.onRedoComplete = onComplete;
    }

    tx.commands.forEach((cmd) => {

      // ✅ restoreCallback kullanan tüm komutlar (Socket, Machine, Lighting, MainPanel, Panel, Bell)
      const isMachineCommand = (cmd.type === 'ADD_MACHINE' || cmd.constructor?.name === 'MachineCommand');
      const isSocketCommand = (cmd.type === 'ADD_SOCKET' || cmd.constructor?.name === 'SocketCommand');
      const isLightingCommand = (cmd.type === 'ADD_LIGHTING' || cmd.constructor?.name === 'LightingCommand');
      const isMainPanelCommand = (cmd.type === 'ADD_MAIN_PANEL' || cmd.constructor?.name === 'MainPanelCommand');
      const isPanelCommand = (cmd.type === 'ADD_PANEL' || cmd.constructor?.name === 'PanelCommand');
      const isBellCommand = (cmd.type === 'ADD_BELL' || cmd.constructor?.name === 'BellCommand');

      const isAsyncCommand = isMachineCommand || isSocketCommand || isLightingCommand ||
                             isMainPanelCommand || isPanelCommand || isBellCommand;

      if (isAsyncCommand && cmd.restoreCallback && typeof cmd.restoreCallback === 'function') {
        try {
          // sceneData'ya onRedoComplete callback'ini ekle
          const enhancedSceneData = {
            ...cmd.sceneData,
            onRedoComplete: () => this.notifyRedoComplete()
          };
          cmd.restoreCallback(cmd.restoreVertices, cmd.restoreSource, true, enhancedSceneData);
        } catch (err) {
          // Hata durumunda da position'ı artır
          if (hasAsyncCommand) {
            this.notifyRedoComplete();
          }
        }
      } else if (cmd?.execute) {
        // Diğer commandlar için normal execute
        cmd.execute(scene, dispatch || this.dispatch);
      }
    });

    this.dispatch(
      addAction({
        type: 'REDO',
        label: tx.label,
        timestamp: Date.now(),
      })
    );

    // 🆕 Sadece sync komutlar için position artır
    if (!hasAsyncCommand) {
      this.position = Math.min(this.history.length - 1, this.position + 1);
      if (onComplete) onComplete();
    }
    // Async komutlar için position artırma notifyRedoComplete'de yapılacak
  }

  // 🧹 Reset
  clear() {
    this.history = [];
    this.position = -1;
    this.dispatch(
      addAction({
        type: 'CLEAR_HISTORY',
        timestamp: Date.now(),
      })
    );
  }
}
